import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import moment from 'moment';

import { Modal, IconButton, Form, Pill, TimeFrame, useTimeFrameComponent, TableButtons } from '@gpstrackit/gtc-react-controls';
import { NotificationContainer } from "Components/Notifications";
import { NotificationManager } from 'Components/Notifications';
import { Timezone } from 'Components/uiControls';
import { Grid, Icon } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { LinearProgress } from 'Components';
import { GetFormatForMoment, getFormattedDateByTimeZone } from "Core/data/Helpers";

import { LogManagerConsole } from 'Core/logManager';
import { reset, closeModal, emissionCheckEventsOpen } from './redux/slice';
import { getInitialRange } from '../../../../../../../video/.globals/helpers';
import useExport from "Components/Table/Export";

export const EmissionCheckEvents = ({ onRefresh, defaultFrame, getStatusColor, getLastScanStatusColor, statuses, scanStatuses }) => {

  //constants
  const { messages, locale } = useIntl();
  const formRef = Form.useForm();
  const dispatch = useDispatch();

  //Redux States
  const lang = useSelector((state) => state.commomRedux.language);
  const user = useSelector(state => state.securityRedux.user);
  const { loading, data, error, result, open } = useSelector(state => state.emissionCheckEventsRedux);
  const { dateformat, timeZone } = user;

  //Local States
  const tableRefEvt = useRef();

  const [id, setId] = useState();
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState(() => getInitialRange(defaultFrame));
  const [eventsPage, setEventsPage] = useState([]);
  const [viewErrors, setViewErrors] = useState([]);

  const timeFrameOptions = useMemo(() => [
    { id: "today", label: messages['date_last_today'] },
    { id: "week", label: messages['date_this_week'] },
    { id: "yesterday", label: messages['date_last_yesterday'] },
    { id: "last15days", label: messages['date_last_15days'] },
    { id: "lastWeek", label: messages['date_last_week'] },
    { id: "lastMonth", label: messages['date_last_month'] },
    { id: "custom", label: messages['date_custom'] }
  ], [locale]);

  const timeFrame = useTimeFrameComponent(timeFrameOptions, defaultFrame);

  //callbackks
  const onClose = useCallback((refresh) => {
    dispatch(closeModal());
    timeFrame.defaultvalue(defaultFrame);
    setId(undefined);
    setPage(1);
    setDateRange(getInitialRange(defaultFrame));
    setEventsPage([]);
    setViewErrors([]);
    onRefresh && onRefresh(refresh);
    dispatch(reset());
  }, [defaultFrame, dispatch, onRefresh, timeFrame])

  const handleChangePage = useCallback((_page, _dateRage) => {
    const params = {
      "fields": ["carbScheduleId"],
      "terms": [data?.item.id],
      "range": {
        "fields": ["scanDate"],
        "GTE": _dateRage.start,
        "LTE": _dateRage.end
      }
    }
    setPage(_page);
    setDateRange(_dateRage);
    if (_dateRage.start) {
      dispatch(emissionCheckEventsOpen({ item: data?.item, conditions: params, limit: (_page * 10) }));
    } else {
      setEventsPage([]);
    }
  })

  //effects
  useEffect(() => {
    LogManagerConsole("EmissionCheck", "edit", "data", data, "result", result, "error", error);

    //validate if the data is not empty and the id is not set
    if (data && !id) {
      setId(data.id);
      formRef.setValues({ firstName: data.firstName, lastName: data.lastName });
      let events = structuredClone(data?.events || []);
      events.sort((a, b) => new Date(b.scanDate) - new Date(a.scanDate));
      setEventsPage(events);
    }

    //validate if the result is not empty
    if (result) {
      dispatch(reset({ result: null }));

      NotificationManager.success(
        messages['success'],
        messages['emissionCheck_saveSuccessfully'],
        3000,
        null,
        null,
        'success',
      );

      onClose(true);
    }

    //validate if the error is not empty
    if (error) {
      dispatch(reset({ error: null }));
      NotificationManager.error(
        error?.error || messages['errorMessage'],
        messages['error'],
        3000,
        null,
        null,
        'error',
      );
    }

  }, [data, result, error]);

  const getHeaders = () => {
    return [
      messages["emissionCheck_scanDate"] + " / " + messages["emissionCheck_registerDate"],
      messages["emissionCheck_status"],
      messages["emissionCheck_error"]
    ];
  };

  const getDataToExport = (rows) => {
    let data = [];
    eventsPage.forEach((event) => {
      data.push([
        getFormattedDateByTimeZone(event.scanDate, timeZone, messages, `${GetFormatForMoment(dateformat)} hh:mm`, false, lang) || '',
        scanStatuses.find(s => s.id == event.scanStatus)?.label || event.scanStatus,
        event.errorMessage
      ]);
    });

    return data;
  };

  const exportCSVPdf = useExport(
    getHeaders,
    getDataToExport,
    [],
    [],
    null,
    '',
    "landscape",
    true
  );

  return (
    <Modal
      title={data?.item?.unit?.label}
      open={open}
      handleClose={onClose}
      position="right"
      size="small"
      loading={loading}
      closeButton={true}
    >
      <div className="emissionCheck-events" style={{ padding: '15px' }}>
        <div className="emissionCheck-events-container">

          <Grid
            container
            direction="row"
            style={{ marginTop: '5px' }}
          >
            <Grid item xs={6} sm={6} className='emissionCheck-subTitle'>
              <Pill
                key='vin'
                item={{ label: messages['emissionCheck_vin'] + ": " + (data?.item?.asset?.vin || ''), color: "green" }}
                clickable={false}
                style={{ border: '0', width: '100%', textAlign: 'center', margin: '0px 0 20px 0' }}
                label="pill-label-text"
                size="filled"
              />
            </Grid>
            <Grid item xs={6} sm={6}>
              <Pill
                item={{ label: statuses.find(s => s.id == data?.item?.status)?.label, color: getStatusColor(data?.item?.status) }}
              />
            </Grid>
          </Grid>

          <div className="time-frame-container" style={{ padding: '10px 0' }}>
            <Grid
              container
              direction="row"
            >
              <Grid item xs={11} sm={11}>
                <TimeFrame
                  {...timeFrame}
                  placeholder={messages['emissionCheck_nextDeadline']}
                  filterSwitch={false}
                  withTime={true}
                  dateFormat={"MM/DD/YYYY hh:mm A"}
                  openDateRangePickerModalAutomatically={false}
                  onChangeSelect={(option) => {
                    const _option = option ? option : { id: defaultFrame };
                    timeFrame.onChangeSelect(_option);
                    const range = _option.id == 'custom' ? {} : getInitialRange(_option.id)
                    handleChangePage(1, range);
                  }}
                  onChangeDateRange={(start, end) => {
                    handleChangePage(1, { start, end });
                  }}
                  maxDate={moment()}
                />
              </Grid>
              <Grid item xs={1} sm={1} style={{ display: 'flex', alignItems: 'center' }}>
                <TableButtons
                  messages={messages}
                  tableRef={tableRefEvt}
                  buttons={['export']}
                  onExport={(type) => {
                    if (eventsPage?.length) {
                      if (type === "Csv") {
                        exportCSVPdf.exportCSV();
                      } else if (type === "Pdf") {
                        exportCSVPdf.exportPDF();
                      }
                    } else {
                      NotificationManager.info(
                        messages['exportInProcessBody'],
                        messages['exportInProcessTitle'],
                        4000,
                        null,
                        null,
                        'info',
                        null
                      );
                    }
                  }}
                />
              </Grid>
            </Grid>
          </div>

          <InfiniteScroll
            height={350}
            dataLength={eventsPage.length}
            next={() => {
              handleChangePage(page + 1, dateRange);
            }}
            hasMore={eventsPage.length < data?.total}
            loader={<span />}
            endMessage={
              <div style={{ textAlign: "center" }}>
                {
                  !loading &&
                  <span style={{ fontSize: 13, color: 'grey', marginTop: '15px' }}>
                    {
                      data?.total == 0 &&
                      <>
                        {messages['noItemsFound']}
                      </>
                    }
                    {
                      (eventsPage.length > 0 && eventsPage.length == data?.total) &&
                      <div style={{ marginTop: '15px' }}>
                        {messages['noMoreItems']}
                      </div>
                    }
                  </span>
                }
              </div>
            }
          >
            {eventsPage.map((event) => (
              <div key={event.id} className="comments-item" style={{ marginTop: '10px' }}>
                <Grid
                  container
                  direction="row"
                  key={event.id}
                  sx={{ justifyContent: "space-between", alignItems: "center" }}
                >
                  <Grid item xs={8} sm={8}>
                    {event.scanStatus == 'REQUESTED' ? messages['emissionCheck_scanDate'] : messages['emissionCheck_registerDate']}:
                  </Grid>
                  <Grid item xs={4} sm={4}>
                    <Pill item={{ label: scanStatuses.find(s => s.id == event.scanStatus)?.label, color: getLastScanStatusColor(event.scanStatus) }} />
                  </Grid>

                  <Grid item xs={12} style={{ marginBottom: 5 }}>
                    <Timezone
                      date={event.scanDate}
                      dateFormat={dateformat}
                      timeFormat="HH:mm:ss"
                      timezone={timeZone}
                    />
                  </Grid>
                  {
                    event.errorMessage && <>
                      <Grid item xs={12} style={{ marginBottom: 5, display: 'flex', alignItems: "center" }}>
                        <div>
                          <span className='emissionCheck-events_viewError' onClick={() => {
                            let errors = structuredClone(viewErrors);
                            if (errors.includes(event.id)) {
                              errors = errors.filter(item => item !== event.id);
                            } else {
                              errors.push(event.id);
                            }
                            setViewErrors(errors);
                          }}>
                            {viewErrors?.includes(event.id) ? messages['emissionCheck_hideError'] : messages['emissionCheck_viewError']}
                          </span>
                        </div>
                        {event.reportFilename &&
                          <div>
                            <a href={event.reportFilename} target="_blank" rel="noopener noreferrer">
                              <IconButton
                                tooltip={messages["emissionCheck_download_file"]}
                                placement="bottom"
                                variant="secondary"
                                icon="article"
                                size="small"
                              />
                            </a>
                          </div>
                        }
                      </Grid>
                      {viewErrors.includes(event.id) &&
                        <div className='emissionCheck-events_error'>
                          {event.errorMessage}
                        </div>
                      }
                    </>
                  }
                </Grid>
              </div>
            )
            )}
            {
              (loading && page !== 1) && <LinearProgress color="primary" />
            }
          </InfiniteScroll>
        </div>
      </div>
      <NotificationContainer />
    </Modal>
  );
}

export default EmissionCheckEvents;
